import React from 'react';
import { DownloadOutlined } from '@ant-design/icons';
import { Button, DatePicker, Input, Select, Tag } from 'antd';

import { fundName, hasFilters } from './register.js';

const { RangePicker } = DatePicker;

/**
 * The filter bar the Transactions and History views share: the fund (the
 * same choice as the fund cards - changing it changes the route), the holder
 * (chosen from a holder's drawer or a row, shown as a tag to clear), a type,
 * a source (Transactions only), a holder number typed in (Transactions only)
 * and a dealing-date range. Every change reads the list again from its first page.
 *
 * @param {{ funds: object[], fundId: string|null, onFund: Function, holder: object|null, onHolder: Function,
 *   holderNo?: string|null, onHolderNo?: Function, txnType: string|null, typeOptions: object[], onType: Function,
 *   source?: string|null, sourceOptions?: object[]|null, onSource?: Function, range: object[]|null, onRange: Function,
 *   download?: boolean, downloading?: boolean, onDownload?: Function, onClear: Function }} props
 */
const RegisterFilters = ({
    funds, fundId, onFund, holder, onHolder, holderNo = null, onHolderNo, txnType, typeOptions, onType,
    source = null, sourceOptions = null, onSource, range, onRange, download = false, downloading = false, onDownload, onClear
}) => {
    const filtered = hasFilters({ holder, holderNo, txnType, source, from: range?.[0], to: range?.[1] });
    return (
        <div className="register-filters">
            <div className={'register-filter-controls' + (download ? ' register-transaction-filter-controls' : '')}>
                <Select
                    aria-label="Fund"
                    className="register-filter-fund"
                    value={fundId ?? ''}
                    onChange={(v) => onFund(v || null)}
                    options={[{ value: '', label: 'Every fund' }, ...funds.map((f) => ({ value: f.fundId, label: f.fundId + ' ' + fundName(f) }))]}
                />
                <Select aria-label="Type" allowClear placeholder="Any Type" className="register-filter-type" value={txnType ?? undefined}
                    onChange={(v) => onType(v ?? null)} options={typeOptions} />
                {sourceOptions && (
                    <Select aria-label="Source" allowClear placeholder="Any Source" className="register-filter-source" value={source ?? undefined}
                        onChange={(v) => onSource(v ?? null)} options={sourceOptions} />
                )}
                {onHolderNo && (
                    <Input aria-label="Holder" allowClear placeholder="Holder" className="register-filter-holder" maxLength={64}
                        value={holderNo ?? ''} onChange={(e) => onHolderNo(e.target.value)} />
                )}
                <RangePicker aria-label="Effective dates" className="register-filter-dates" placeholder={['Start date', 'End date']} value={range} format="YYYY-MM-DD" allowEmpty={[true, true]} onChange={(r) => onRange(r ?? null)} />
                {download && (
                    <Button aria-label="Download transactions" className="register-table-download" icon={<DownloadOutlined />} onClick={onDownload}
                        loading={downloading} disabled={downloading}>
                        Download
                    </Button>
                )}
            </div>
            {(holder || filtered) && (
                <div className="register-filter-actions">
                    {holder && (
                        <Tag closable onClose={(e) => { e.preventDefault(); onHolder(null); }} className="register-holder-tag">
                            {holder.holderNo}{holder.fullName ? ' · ' + holder.fullName : ''}
                        </Tag>
                    )}
                    {filtered && <Button type="link" onClick={onClear}>Clear filters</Button>}
                </div>
            )}
        </div>
    );
};

/** A RangePicker's pair as the API's from and to (YYYY-MM-DD), either end open. */
export const rangeDates = (range) => ({ from: range?.[0]?.format('YYYY-MM-DD') ?? null, to: range?.[1]?.format('YYYY-MM-DD') ?? null });

export default RegisterFilters;
